/**
 * QuickRepliesPanel
 *
 * Admin CRUD for the message quick-reply chips shown to guests and hosts.
 */

import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '@/contexts';
import {
  fetchAllQuickReplies,
  createQuickReply,
  updateQuickReply,
  deleteQuickReply,
  type QuickReply,
  type QuickReplyVisibility,
} from '@/services/quickReplies';

interface FormState {
  text: string;
  visibleTo: QuickReplyVisibility;
  order: number;
  active: boolean;
}

const EMPTY_FORM: FormState = {
  text: '',
  visibleTo: 'all',
  order: 100,
  active: true,
};

export const QuickRepliesPanel: React.FC = () => {
  const { user } = useAuth();
  const [replies, setReplies] = useState<QuickReply[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [isSaving, setIsSaving] = useState(false);

  const load = async () => {
    setIsLoading(true);
    try {
      const all = await fetchAllQuickReplies();
      setReplies(all);
    } catch (err) {
      console.error('Failed to load quick replies:', err);
      toast.error('Failed to load quick replies');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleEdit = (reply: QuickReply) => {
    setEditingId(reply.id);
    setForm({
      text: reply.text,
      visibleTo: reply.visibleTo,
      order: reply.order,
      active: reply.active,
    });
  };

  const handleCancel = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.text.trim()) {
      toast.error('Please enter the reply text');
      return;
    }
    setIsSaving(true);
    try {
      if (editingId) {
        await updateQuickReply(editingId, form);
        toast.success('Quick reply updated');
      } else {
        await createQuickReply(form, user?.uid || 'unknown');
        toast.success('Quick reply added');
      }
      await load();
      handleCancel();
    } catch (err) {
      console.error('Failed to save quick reply:', err);
      toast.error('Failed to save quick reply');
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleActive = async (reply: QuickReply) => {
    try {
      await updateQuickReply(reply.id, { active: !reply.active });
      await load();
    } catch (err) {
      console.error('Failed to toggle reply:', err);
      toast.error('Failed to update');
    }
  };

  const handleDelete = async (reply: QuickReply) => {
    if (!confirm(`Delete "${reply.text}"?`)) return;
    try {
      await deleteQuickReply(reply.id);
      toast.success('Quick reply deleted');
      await load();
    } catch (err) {
      console.error('Failed to delete reply:', err);
      toast.error('Failed to delete');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Quick replies</h2>
        <p className="text-sm text-gray-500">
          Canned message templates shown as chips above the message textarea.
          Tapping a chip fills the message; users can still edit or type their own.
        </p>
      </div>

      {/* Form */}
      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-xl border border-gray-200 p-5 space-y-4"
      >
        <h3 className="font-medium text-gray-900">
          {editingId ? 'Edit reply' : 'Add a new reply'}
        </h3>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Reply text
          </label>
          <input
            type="text"
            value={form.text}
            onChange={(e) => setForm({ ...form, text: e.target.value })}
            placeholder="e.g. Yes, the dates are available"
            maxLength={200}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
          <p className="text-xs text-gray-400 mt-1">
            {form.text.length}/200 characters
          </p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Show to
            </label>
            <select
              value={form.visibleTo}
              onChange={(e) =>
                setForm({ ...form, visibleTo: e.target.value as QuickReplyVisibility })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="all">Everyone</option>
              <option value="host">Hosts only</option>
              <option value="guest">Guests only</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Order
            </label>
            <input
              type="number"
              value={form.order}
              onChange={(e) =>
                setForm({ ...form, order: parseInt(e.target.value, 10) || 0 })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            <p className="text-xs text-gray-400 mt-1">Lower numbers show first</p>
          </div>
        </div>

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={form.active}
            onChange={(e) => setForm({ ...form, active: e.target.checked })}
            className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <span className="text-sm text-gray-700">Active (visible to users)</span>
        </label>

        <div className="flex gap-2 pt-2">
          <button
            type="submit"
            disabled={isSaving}
            className="px-4 py-2 bg-primary-500 text-white font-medium rounded-lg hover:bg-primary-600 disabled:opacity-50"
          >
            {isSaving ? 'Saving…' : editingId ? 'Save changes' : 'Add reply'}
          </button>
          {editingId && (
            <button
              type="button"
              onClick={handleCancel}
              className="px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
          )}
        </div>
      </form>

      {/* List */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">Loading…</div>
        ) : replies.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No quick replies yet. Add one above.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-gray-700">Text</th>
                <th className="text-left px-4 py-3 font-medium text-gray-700">
                  Visible to
                </th>
                <th className="text-left px-4 py-3 font-medium text-gray-700">
                  Order
                </th>
                <th className="text-left px-4 py-3 font-medium text-gray-700">
                  Status
                </th>
                <th className="text-right px-4 py-3 font-medium text-gray-700">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {replies.map((r) => (
                <tr key={r.id} className="border-b border-gray-100 last:border-0">
                  <td className="px-4 py-3 text-gray-900">{r.text}</td>
                  <td className="px-4 py-3 text-gray-600 capitalize">
                    {r.visibleTo === 'all' ? 'Everyone' : r.visibleTo + 's'}
                  </td>
                  <td className="px-4 py-3 text-gray-600">{r.order}</td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => handleToggleActive(r)}
                      className={`px-2 py-1 text-xs font-medium rounded ${
                        r.active
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-500'
                      }`}
                    >
                      {r.active ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => handleEdit(r)}
                      className="text-primary-600 hover:text-primary-700 font-medium mr-3"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(r)}
                      className="text-red-600 hover:text-red-700 font-medium"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default QuickRepliesPanel;
